import type { Services } from "../services/services";
import type { Disposable } from "./lifecycle";
import { useEffect, useRef } from "react";
import { useServices } from "../services/services-context";



export abstract class Controller implements Disposable {
	#services: Services | null = null;

	private initialised = false;
	#disposed = false;

	private injectServices(services: Services): void {
		this.#services = services;
	}

	private runInit() {
		if(this.initialised) {
			return;
		}
		this.#disposed = false;
		this.initialised = true;
		this.initialise();
	}

	initialise(): void { /**/ }

	private runDispose() {
		if(!this.initialised) {
			return;
		}

		this.initialised = false;
		this.#disposed = true;
		this.dispose();
	}

	dispose(): void { /**/ }

	/**
	 * Runs initialisation for a child Controller owned by this Controller.
	 */
	protected initialiseChild(...childControllers: Controller[]): void {
		for(const childController of childControllers) {
			childController.injectServices(this.services);
			childController.runInit();
		}
	}

	protected disposeChild(...childControllers: Controller[]): void {
		for(const childController of childControllers) {
			childController.runDispose();
		}
	}

	protected assertInitialised<T>(value: T | undefined | null): T {
		if(!value) {
			throw new Error(`${this.#controllerName} was not initialised.`);
		}

		return value;
	}

	protected get services(): Services {
		return this.assertInitialised(this.#services);
	}

	get #controllerName(): string {
		return Object.getPrototypeOf(this)?.constructor?.name ?? "Controller";
	}

	get disposed(): boolean {
		return this.#disposed;
	}
}

export type ControllerConstructor<T extends Controller> = new () => T;
export type ControllerFactory<T extends Controller> = () => T;

const isConstructable = <T extends Controller>(x: ControllerConstructor<T> | ControllerFactory<T>): x is ControllerConstructor<T> => {
	return !!(x as any).prototype?.constructor; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export const useController = <T extends Controller>(factory: ControllerFactory<T> | ControllerConstructor<T>): T => {
	const services = useServices();
	const controllerRef = useRef<T | null>(null);

	if(!controllerRef.current) {
		if(isConstructable(factory)) {
			controllerRef.current = new factory();
		} else {
			controllerRef.current = factory();
		}
		controllerRef.current["injectServices"](services);
		controllerRef.current["runInit"]();
	}

	useEffect(() => {
		controllerRef.current?.["runInit"]();
		return () => controllerRef.current?.["runDispose"]();
	}, []);

	return controllerRef.current;
};
